import {Component, OnInit} from "@angular/core";
import {BaseComponent} from "../../base.component";
import {ShareableService} from "./shareable.service";
import {LoggerService} from "../../common/logger.service";
import {SessionService} from "../../common/session.service";
import {CartService} from "../../cart/cart.service";
import {MarkupService} from "../../common/markup.service";
import {CatalogService} from "../../cart/catalog/catalog.service";
import {IShareParams} from "../social-models";
import {Constants} from "../../constants";
import {LocaleUtil, uxCartRouterCommands} from "../../util/locale-util";
import {ShareableUtil} from "../shareable-util";
import {Util} from "../../util/util";
import {IShareableCart} from "../../cart/cart-models";
import {ICategory, ITag} from "../../cart/catalog/catalog-models";
import * as _ from "lodash";
import {SocialWelcomeComponent} from "./welcome/social-welcome.component";
import {find, map, switchMap, take} from "rxjs/operators";
import {CategoryService} from "../../category/category.service";
import {ActivatedRoute, Router} from "@angular/router";
import {combineLatest, Observable, of} from "rxjs";
import {BsModalRef, BsModalService} from "ngx-bootstrap/modal";
import {IMarketingPage} from "../../marketing-pages/marketing-page.models";
import {MarketingPageService} from "../../marketing-pages/marketing-page.service";
import {IRestResponse} from "../../common/rest-response.model";
import {UserService} from "../../common/user.service";
import {ModalUtil} from "../../modal/modal-util";
import {Customer} from 'src/app/checkout/models/customer/customer';
import {AuthUtil} from "../../auth/auth-util";

@Component({
    selector: 'shareable',
    template: `<div></div>`,
    standalone: false
})
export class ShareableComponent extends BaseComponent implements OnInit {

    params: IShareParams = {};
    sharedParams: any;
    modalRef: BsModalRef;

    constructor(
        private shareableService: ShareableService,
        loggerService: LoggerService,
        private userService: UserService,
        private sessionService: SessionService,
        private cartService: CartService,
        private markupService: MarkupService,
        private catalogService: CatalogService,
        private categoryService: CategoryService,
        private modalService: BsModalService,
        private marketingPageService: MarketingPageService,
        private router: Router,
        private route: ActivatedRoute,
    ) {
        super(userService, loggerService);
    }

    ngOnInit() {
        this.getParams();
    }

    getParam(param: string): string {
        if (this.sharedParams[param]) {
            return this.sharedParams[param];
        }
    }

    getParams(): void {
        combineLatest([this.route.params, this.route.queryParams]).pipe(map(([params, queryParams]) => {
            this.sharedParams = {...params, ...queryParams};
            this.params.cartInit = this.getParam(Constants.CART_INIT_PARAM);
            this.params.locale = this.getParam(Constants.LOCALE_PARAM);
            this.params.shareId = this.getParam(Constants.SHARE_ID_PARAM);
            this.params.shareType = this.getParam(Constants.SHARE_TYPE_PARAM);
            this.params.shareListId = Util.parseInt(this.getParam(Constants.SHARE_LIST_STATE_PARAM));

            //grandfathered params. the correct format is to use shareId and shareType.
            this.params.usanaId = this.getParam(Constants.USANA_ID_STATE_PARAM);
            this.params.categoryId = Util.parseInt(this.getParam(Constants.CATEGORY_ID_STATE_PARAM));
            this.params.tagId = Util.parseInt(this.getParam(Constants.TAG_ID_STATE_PARAM));
            this.params.marketingPageId = this.getParam(Constants.MARKETING_PAGE_ID_STATE_PARAM);
        })).subscribe(() => this.checkParams());
    }

    checkParams() {
        this.log.debug("hit checkParams state.params", this.sharedParams);

        if (this.params) {
            //we need to set the locale to the shareERs locale if it's prerender.io requesting content
            if (Util.isPrerender(window) && !!this.params.locale) {
                this.log.debug("checkParams", "this.isPrerender && !!stateLocale", this.params.locale);
                this.sessionService.updateLocale(LocaleUtil.getUsanaLocale(this.params.locale));
            }

            this.initCart();
        }
    }

    initCart() {
        this.mapParameters();
        const isCartShare = ShareableUtil.isCartShare(this.params);
        let usanaLocale;
        let canSaveForLater;
        this.userService.getShopUser().pipe(
            switchMap((user) => {
                usanaLocale = LocaleUtil.getUrlLocale(user.usanaLocale);
                canSaveForLater = !AuthUtil.isRetail(user) && user.currentCountry !== Constants.COUNTRY_CN && !AuthUtil.isEvent(user);
                return this.prepareShareable(isCartShare);
            })
        ).subscribe(() => {
            if (isCartShare) {
                this.handleCartShareable(usanaLocale, canSaveForLater);
            } else {
                if (!Util.isEmpty(this.params.shareId)) {
                    if (this.params.shareType == Constants.SHARE_TYPE_CATEGORY) {
                        this.handleCategoryShareable(Util.parseInt(this.params.shareId, 10));
                    } else if (this.params.shareType == Constants.SHARE_TYPE_TAG) {
                        this.handleTagShareable(Util.parseInt(this.params.shareId, 10));
                    } else if (this.params.shareType == Constants.SHARE_TYPE_MARKETING_PAGE) {
                        this.handleMarketingPageShareable(this.params.shareId);
                    } else if (this.params.shareType == Constants.SHARE_TYPE_ENROLL) {
                        this.handleEnrollShareable(this.params.shareId);
                    }
                } else {
                    //everything else defaults to the landing page
                    this.handleRedirectShareable(Constants.LANDING_STATE);
                }
            }
        });
    }

    mapParameters() {
        //map grandfathered parameters if they exist
        if (!Util.isEmpty(this.params.usanaId)) {
            this.params.shareId = this.params.usanaId;
            this.params.shareType = Constants.SHARE_TYPE_SHARE_ONE;
        } else if (!Util.isEmpty(this.params.categoryId)) {
            this.params.shareId = this.params.categoryId.toString();
            this.params.shareType = Constants.SHARE_TYPE_CATEGORY;
        } else if (!Util.isEmpty(this.params.tagId)) {
            this.params.shareId = this.params.tagId.toString();
            this.params.shareType = Constants.SHARE_TYPE_TAG;
        } else if (!Util.isEmpty(this.params.marketingPageId)) {
            this.params.shareId = this.params.marketingPageId;
            this.params.shareType = Constants.SHARE_TYPE_MARKETING_PAGE;
        }
    }

    prepareShareable(showAddToCart: boolean): Observable<any> {
        //first time through, show welcome and allow them to change locale, if locale not changed continue with shareable action
        if (!ModalUtil.hasSeenSocialWelcomeModal() && !Util.isPrerender(window) && !this.authenticated) {
            return this.sessionService.retrieveCustomer(false).pipe(
                switchMap((customer) => {
                    this.openWelcomeDialog(showAddToCart, customer);
                    return this.modalService.onHide.pipe(take(1));
                })
            );
        } else {
            //if locale is changed the page has released, don't show welcome, just continue with shareable action
            return of(null);
        }
    }

    handleCategoryShareable(categoryId: number) {
        if (!Util.isEmpty(categoryId)) {
            this.categoryService.getNavCategories().pipe(
                find(val => val.length > 0),
                map(categories => {
                    _.forEach(categories, (category: ICategory) => {
                        if (category.id === categoryId) {
                            this.markupService.setOgSingleCategoryPage(category, this.params.locale);
                            return;
                        }
                        _.forEach(category.children, (subCategory: ICategory) => {
                            if (subCategory.id === categoryId) {
                                this.markupService.setOgSingleCategoryPage(subCategory, this.params.locale);
                                return;
                            }
                        });
                    });
                })
            ).subscribe();
            if (!Util.isPrerender(window)) {
                const userLocale = LocaleUtil.getUrlLocale(this.userService.getShopUserSnapshot().usanaLocale);
                this.redirectUser(Constants.CATALOG_STATE, categoryId, userLocale);
            }
        }
    }

    handleTagShareable(tagId: number) {
        if (!Util.isEmpty(tagId)) {
            this.catalogService.getTagById(tagId).subscribe((tag: ITag) => {
                if (tag) {
                    this.markupService.setOgSingleTagPage(tag, this.params.locale);
                }
            });
            if (!Util.isPrerender(window)) {
                const userLocale = LocaleUtil.getUrlLocale(this.userService.getShopUserSnapshot().usanaLocale);
                this.redirectUser(Constants.TAG_CATALOG_STATE, tagId, userLocale);
            }
        }
    }

    handleMarketingPageShareable(marketingPageId: string) {
        if (!Util.isEmpty(marketingPageId)) {
            this.marketingPageService.getPage(marketingPageId, Constants.MARKETING_PAGE_TYPE_HCP).subscribe((page: IMarketingPage) => {
                if (!!page && !!page.cartImage) {
                    this.markupService.setOgMarketingPage(page, this.params.locale);
                } else {
                    this.markupService.setOgDefault();
                }
            }, () => {
                this.markupService.setOgDefault();
            });
            if (!Util.isPrerender(window)) {
                const userLocale = LocaleUtil.getUrlLocale(this.userService.getShopUserSnapshot().usanaLocale);
                this.redirectUser(Constants.MARKETING_PAGE_STATE, marketingPageId, userLocale);
            }
        }
    }

    handleRedirectShareable(toState: string) {
        if (!Util.isPrerender(window) && !Util.isEmpty(toState)) {
            const userLocale = LocaleUtil.getUrlLocale(this.userService.getShopUserSnapshot().usanaLocale);
            this.redirectUser(toState, 0, userLocale);
        }
    }

    handleCartShareable(userLocale: string, canSaveForLater: boolean = false) {
        this.shareableService.getCartProducts(ShareableUtil.getShareCartInit(this.params)).subscribe((response: IRestResponse) => {
            if (response.success) {
                const shareableCart: IShareableCart = response.message;

                if (ShareableUtil.isShareListShare(this.params)) {
                    this.markupService.setOgShareList(shareableCart, this.params.locale);
                    if (!Util.isPrerender(window)) {
                        if (shareableCart.products.length > 0) {
                            this.shareableService.addShareListProductsToCart(shareableCart.products, canSaveForLater, (existingProductsMoved: boolean) => {
                                this.redirectUser(Constants.CART_OVERVIEW_STATE, 0, userLocale, {
                                    shareListArrival: true,
                                    existingProductsMoved: existingProductsMoved
                                });
                            });
                            this.shareableService.displayUnavailableProductsMessage(shareableCart);
                        } else {
                            this.redirectUser(Constants.CATALOG_STATE, -1, userLocale);
                            this.shareableService.displayUnavailableProductsMessage(shareableCart);
                        }
                    }
                } else {
                    this.cartService.fetchCart().subscribe();
                    if (ShareableUtil.isShareOneShare(this.params)) {
                        this.markupService.setOgSingleProductByProducts(this.params.shareId, shareableCart.products);
                    }
                    if (!Util.isPrerender(window)) {
                        if (shareableCart.products.length > 0) {
                            this.redirectUser(Constants.SHARE_CART_STATE, 0, userLocale, ShareableUtil.getShareableCartQueryParam(this.params));
                        } else {
                            this.redirectUser(Constants.CATALOG_STATE, -1, userLocale);
                            this.shareableService.displayUnavailableProductsMessage(shareableCart);
                        }
                    }
                }
            } else {
                this.log.warn("cart init backend returned not success for shareables", response);
                this.shareableService.showErrorMessage();
            }
        }, (error: any) => {
            this.log.error("cart init backend error for shareables", error);
            this.shareableService.showErrorMessage();
        });
    }

    handleEnrollShareable(enrollState: string) {
        const allowedStates = [
            Constants.ENROLL_AFFILIATE_STATE,
            Constants.ENROLL_ASSOCIATE_STATE,
            Constants.ENROLL_PC_STATE
        ];
        if (allowedStates.includes(enrollState)) {
            this.handleRedirectShareable(Constants.ENROLL_STATE + "/" + enrollState);
        }
    }

    openWelcomeDialog(showAddToCart: boolean, customer: Customer): BsModalRef {
        ModalUtil.rememberSocialWelcomeModalOpened();
        const initialState = {
            showAddToCart,
            shareId: this.params.shareId,
            customerType: customer?.customerType.code,
            freeShippingEligible: customer?.config.freeShippingEligible
        };

        this.modalRef = this.modalService.show(SocialWelcomeComponent, Object.assign({}, {initialState}));

        return this.modalRef;
    }

    redirectUser(fragment: string, id: number | string, locale: string, queryParams?: any): void {
        const pathParts = fragment.includes("/") ? fragment.split("/") : [fragment];
        const segments = id ? [...pathParts, id] : pathParts;
        this.router.navigate(uxCartRouterCommands(locale, ...segments), {queryParams: queryParams});
    }
}
